import { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import {
  CopyHex, EmptyState, ErrorBanner, FilterChips, Investor, RowsSkeleton, SearchInput, SectionHeader,
  StatusBadge, WITHDRAWAL_STATUS, fmtDateTime, fmtNum,
} from "./bcShared";

const COLS = "md:grid-cols-[0.9fr,1.4fr,1.2fr,0.6fr,1.1fr,1fr,1fr]";

export default function BlockchainWithdrawals({ withdrawals, collections, loading, error, onRetry }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const byToken = useMemo(() => Object.fromEntries(collections.map((c) => [c.tokenId, c])), [collections]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return withdrawals.filter((w) => {
      if (filter !== "all" && w.status !== filter) return false;
      if (!needle) return true;
      return [w.id, w.fromAddress, w.toAddress, w.txHash, w.investor?.name, w.investor?.email, byToken[w.tokenId]?.symbol]
        .some((v) => v?.toLowerCase().includes(needle));
    });
  }, [withdrawals, filter, q, byToken]);

  const count = (s) => withdrawals.filter((w) => w.status === s).length;

  return (
    <div className="zc-card overflow-hidden zc-stack-table" data-testid="bc-withdrawals">
      <SectionHeader title="Withdrawals to external wallets" count={withdrawals.length}>
        <div className="flex flex-wrap items-center gap-3">
          <FilterChips
            value={filter}
            onChange={setFilter}
            options={[
              { id: "all", label: "All", count: withdrawals.length },
              { id: "COMPLETED", label: "Completed", count: count("COMPLETED") },
              { id: "PENDING", label: "Pending", count: count("PENDING") },
              { id: "FAILED", label: "Failed", count: count("FAILED") },
            ]}
          />
          <SearchInput value={q} onChange={setQ} placeholder="Search investor, address or tx…" />
        </div>
      </SectionHeader>

      {error && <ErrorBanner message={error} onRetry={onRetry} />}

      <div className={`hidden md:grid ${COLS} gap-4 zc-stack-head px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold`}>
        <div>Date</div><div>Investor</div><div>Token</div><div>Amount</div><div>Destination</div><div>Status</div><div>Tx</div>
      </div>

      {loading && withdrawals.length === 0 && <RowsSkeleton />}
      {!loading && !error && rows.length === 0 && (
        <EmptyState icon={ArrowUpRight} title={withdrawals.length ? "No withdrawals match" : "No withdrawals yet"} hint={withdrawals.length ? "Try a different filter or search." : "Appears when investors move tokens out of their custodial wallet."} />
      )}

      {rows.map((w) => {
        const st = WITHDRAWAL_STATUS[w.status] || { label: w.status, tone: "neutral" };
        const col = byToken[w.tokenId];
        return (
          <div key={w.id} className={`zc-stack-row md:grid ${COLS} md:items-center gap-4 px-4 sm:px-6 py-3.5 border-b border-[var(--zc-border)] last:border-0 text-sm`}>
            <div data-label="Date" className="text-xs text-[var(--zc-text-2)]">{fmtDateTime(w.createdAt)}</div>
            <div data-label="Investor" className="min-w-0"><Investor investor={w.investor} fallbackAddress={w.fromAddress} /></div>
            <div data-label="Token" className="min-w-0">
              <div className="truncate">{col?.name || `Token #${w.tokenId}`}</div>
              {col && <div className="text-2xs text-[var(--zc-text-3)] mt-0.5"><span className="font-mono">{col.symbol}</span> · #{w.tokenId}</div>}
            </div>
            <div data-label="Amount" className="font-semibold tabular-nums">{fmtNum(w.amount)}</div>
            <div data-label="Destination"><CopyHex value={w.toAddress} label="Destination address" /></div>
            <div data-label="Status" className="min-w-0">
              <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
              {w.errorMessage && <div className="text-2xs text-[var(--zc-danger)] mt-1 line-clamp-2" title={w.errorMessage}>{w.errorMessage}</div>}
            </div>
            <div data-label="Tx"><CopyHex value={w.txHash} label="Transaction hash" /></div>
          </div>
        );
      })}
    </div>
  );
}
